import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { formatApiError } from '@/lib/utils';
import {
  Plus, X, Loader2, Trash2, ChevronLeft, ChevronRight,
  CalendarDays, Clock, Tag, AlignLeft, RefreshCw, Link, Unlink
} from 'lucide-react';

/* ─── Google Calendar widget ─────────────────────────────── */
function GoogleCalWidget({ onSyncDone }) {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [confirmDesconectar, setConfirmDesconectar] = useState(false);

  const cargarEstado = async () => {
    try { const r = await api.get('/api/calendar/estado'); setEstado(r.data); }
    catch {}
    finally { setCargando(false); }
  };

  // Maneja retorno de Google OAuth (?google_ok=1 o ?google_error=...)
  useEffect(() => {
    cargarEstado();
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_ok') === '1') {
      showAlert('Google Calendar conectado correctamente.', 'success');
      window.history.replaceState({}, '', window.location.pathname);
      cargarEstado();
    } else if (params.get('google_error')) {
      showAlert(`Error al conectar Google: ${params.get('google_error')}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const conectar = async () => {
    try {
      const r = await api.get('/api/calendar/auth');
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
      else showAlert('Google OAuth no está configurado en el servidor.');
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al iniciar conexión con Google');
    }
  };

  const desconectar = async () => {
    try {
      await api.delete('/api/calendar/desconectar');
      setConfirmDesconectar(false);
      showAlert('Google Calendar desconectado.', 'info');
      cargarEstado();
    } catch (e) {
      showAlert('Error al desconectar');
    }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.post('/api/calendar/pull');
      showAlert(`Sincronizado: ${r.data.importados} nuevos, ${r.data.actualizados} actualizados.`, 'success');
      if (onSyncDone) onSyncDone();
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al sincronizar');
    } finally { setSincronizando(false); }
  };

  if (cargando) return null;

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-[#0E0E0E] text-xs">
        {estado?.conectado ? (
          <>
            <div className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
            <span className="text-[#888] hidden sm:inline truncate max-w-[140px]">{estado.email || 'Google Cal'}</span>
            <button onClick={sincronizar} disabled={sincronizando}
              className="flex items-center gap-1 text-[#C9A44C] hover:text-[#E0C878] ml-1">
              <RefreshCw className={`w-3.5 h-3.5 ${sincronizando ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Sync</span>
            </button>
            <button onClick={() => setConfirmDesconectar(true)} title="Desconectar Google Calendar"
              className="flex items-center gap-1 text-[#999] hover:text-red-400 ml-1">
              <Unlink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Desconectar</span>
            </button>
          </>
        ) : (
          <button onClick={conectar} className="flex items-center gap-1.5 text-[#888] hover:text-[#C9A44C]">
            <Link className="w-3.5 h-3.5" />
            <span>Conectar Google Calendar</span>
          </button>
        )}
      </div>

      {/* Modal confirmación desconectar */}
      {confirmDesconectar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#111] border border-white/10 rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <p className="text-[#F8F6F0] text-sm mb-4">¿Desconectar Google Calendar? Los eventos ya sincronizados permanecerán.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDesconectar(false)}
                className="px-4 py-2 text-sm text-[#888] hover:text-white transition-colors">
                Cancelar
              </button>
              <button onClick={desconectar}
                className="px-4 py-2 text-sm bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors">
                Desconectar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const fmtHora = (s) => {
  try { return new Date(s).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};
const fmtFull = (s) => {
  try {
    return new Date(s).toLocaleString('es-AR', {
      weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
  } catch { return s || ''; }
};

/* ─── Modal crear evento ─────────────────────────────────────── */
function ModalCrear({ fechaInicial, onClose, onCreated }) {
  const [f, setF] = useState({
    titulo: '',
    inicio: fechaInicial || '',
    fin: '',
    descripcion: '',
    color: '#C9A44C',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      const body = {
        ...f,
        inicio: new Date(f.inicio).toISOString(),
        fin: f.fin ? new Date(f.fin).toISOString() : null,
      };
      await api.post('/api/agenda/eventos', body);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al crear evento'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nuevo evento</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
            <input className={IC + ' pl-10'} placeholder="Título *" value={f.titulo} onChange={set('titulo')} required />
          </div>
          <div>
            <label className="text-[11px] text-[#888] uppercase tracking-wide block mb-1">
              <Clock className="w-3 h-3 inline mr-1" />Inicio *
            </label>
            <input className={IC} type="datetime-local" value={f.inicio} onChange={set('inicio')} required />
          </div>
          <div>
            <label className="text-[11px] text-[#888] uppercase tracking-wide block mb-1">
              <Clock className="w-3 h-3 inline mr-1" />Fin
            </label>
            <input className={IC} type="datetime-local" value={f.fin} onChange={set('fin')} />
          </div>
          <div>
            <label className="text-[11px] text-[#888] uppercase tracking-wide block mb-1">
              <AlignLeft className="w-3 h-3 inline mr-1" />Descripción
            </label>
            <textarea className={IC + ' resize-none'} rows={2} value={f.descripcion} onChange={set('descripcion')} />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[11px] text-[#888] uppercase tracking-wide">Color</label>
            <input type="color" value={f.color} onChange={set('color')}
              className="w-10 h-8 rounded cursor-pointer bg-transparent border-0" />
            <div className="flex gap-2">
              {['#C9A44C', '#4285F4', '#0F9D58', '#DB4437', '#AB47BC'].map((c) => (
                <button key={c} type="button" onClick={() => setF((p) => ({ ...p, color: c }))}
                  className="w-6 h-6 rounded-full border-2 transition-all"
                  style={{ backgroundColor: c, borderColor: f.color === c ? '#fff' : 'transparent' }} />
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear evento'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Agenda page ─────────────────────────────────────────────── */
export default function Agenda() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoy] = useState(new Date());
  const [mes, setMes] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [showCrear, setShowCrear] = useState(false);
  const [fechaCrear, setFechaCrear] = useState('');
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [eventoAEliminar, setEventoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [vista, setVista] = useState('calendario'); // calendario | lista

  const cargar = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/agenda/eventos'); setEventos(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  // Abre el modal de confirmación (guarda el evento a eliminar).
  const eliminar = (ev) => setEventoAEliminar(ev);

  const confirmarEliminar = async () => {
    if (!eventoAEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/api/agenda/eventos/${eventoAEliminar.id}`);
      setEventoAEliminar(null);
      setEventoSeleccionado(null);
      cargar();
    } catch (e) {
      showAlert(formatApiError(e.response?.data?.detail, 'Error al eliminar el evento'));
    } finally { setEliminando(false); }
  };

  /* ─── Datos del mes actual ─────────────────────────────────── */
  const anio = mes.getFullYear();
  const numMes = mes.getMonth();
  const primerDia = new Date(anio, numMes, 1).getDay();
  const diasEnMes = new Date(anio, numMes + 1, 0).getDate();
  const totalCeldas = Math.ceil((primerDia + diasEnMes) / 7) * 7;

  const eventosDelMes = eventos.filter((ev) => {
    const d = new Date(ev.start);
    return d.getFullYear() === anio && d.getMonth() === numMes;
  });

  const eventosDia = (dia) =>
    eventosDelMes.filter((ev) => new Date(ev.start).getDate() === dia);

  const eventosListado = [...eventos].sort((a, b) => new Date(a.start) - new Date(b.start));
  const futuros = eventosListado.filter((ev) => new Date(ev.start) >= new Date());

  const clickDia = (dia) => {
    const d = new Date(anio, numMes, dia, 9, 0);
    const pad = (n) => String(n).padStart(2, '0');
    const s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
    setFechaCrear(s);
    setShowCrear(true);
  };

  const esHoy = (dia) =>
    hoy.getFullYear() === anio && hoy.getMonth() === numMes && hoy.getDate() === dia;

  return (
    <DashboardLayout title="Agenda">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          {/* Navegación de mes */}
          <button onClick={() => setMes(new Date(anio, numMes - 1, 1))}
            className="p-2 rounded-lg text-[#888] hover:text-[#F8F6F0] hover:bg-white/5 transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h2 className="text-base min-w-[140px] text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {MESES[numMes]} {anio}
          </h2>
          <button onClick={() => setMes(new Date(anio, numMes + 1, 1))}
            className="p-2 rounded-lg text-[#888] hover:text-[#F8F6F0] hover:bg-white/5 transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setMes(new Date(hoy.getFullYear(), hoy.getMonth(), 1))}
            className="px-3 py-1.5 text-xs border border-[rgba(201,164,76,0.25)] text-[#C9A44C] rounded-lg hover:bg-[#C9A44C]/10 transition-all ml-1">
            Hoy
          </button>
        </div>
        <div className="flex items-center gap-2">
          <GoogleCalWidget onSyncDone={cargar} />
          <button onClick={() => { setFechaCrear(''); setShowCrear(true); }}
            className="text-[#070707] font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            <Plus className="w-4 h-4" /> Nuevo evento
          </button>
        </div>
      </div>

      {vista === 'calendario' ? (
        /* ─── Vista calendario mensual ─────────────────────── */
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
          {/* Cabecera días */}
          <div className="grid grid-cols-7 border-b border-[rgba(201,164,76,0.1)]">
            {DIAS.map((d) => (
              <div key={d} className="py-2 text-center text-[10px] uppercase tracking-[0.15em] text-[#888]">{d}</div>
            ))}
          </div>
          {/* Celdas */}
          <div className="grid grid-cols-7">
            {Array.from({ length: totalCeldas }).map((_, i) => {
              const dia = i - primerDia + 1;
              const valido = dia >= 1 && dia <= diasEnMes;
              const evs = valido ? eventosDia(dia) : [];
              return (
                <div key={i}
                  onClick={() => valido && clickDia(dia)}
                  className={`min-h-[52px] sm:min-h-[90px] p-1 sm:p-1.5 border-r border-b border-white/[0.04] last-of-type:border-r-0 transition-all ${
                    valido ? 'cursor-pointer hover:bg-white/[0.03]' : 'opacity-30'
                  }`}>
                  {valido && (
                    <>
                      <div className={`text-xs w-6 h-6 flex items-center justify-center rounded-full mb-1 ${
                        esHoy(dia)
                          ? 'bg-[#C9A44C] text-[#070707] font-bold'
                          : 'text-[#ccc]'
                      }`}>{dia}</div>

                      {/* Mobile: solo puntos de color por evento */}
                      {evs.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 sm:hidden">
                          {evs.slice(0, 4).map((ev) => (
                            <span key={ev.id} className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: ev.backgroundColor || '#C9A44C' }} />
                          ))}
                        </div>
                      )}

                      {/* Desktop/tablet: chips con texto */}
                      <div className="space-y-0.5 hidden sm:block">
                        {evs.slice(0, 3).map((ev) => (
                          <div key={ev.id}
                            onClick={(e) => { e.stopPropagation(); setEventoSeleccionado(ev); }}
                            className="truncate text-[10px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: (ev.backgroundColor || '#C9A44C') + '22', color: ev.backgroundColor || '#C9A44C' }}>
                            {fmtHora(ev.start)} {ev.title}
                          </div>
                        ))}
                        {evs.length > 3 && (
                          <div className="text-[9px] text-[#888] px-1">+{evs.length - 3} más</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ─── Vista lista ──────────────────────────────────── */
        <div className="space-y-2">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
          ) : futuros.length === 0 ? (
            <div className="text-center py-20 text-[#666]">
              <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Sin eventos próximos</p>
            </div>
          ) : (
            futuros.map((ev) => (
              <div key={ev.id}
                onClick={() => setEventoSeleccionado(ev)}
                className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4 flex items-center gap-4 hover:border-[#C9A44C]/30 cursor-pointer transition-all">
                <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: (ev.backgroundColor || '#C9A44C') + '15', borderColor: (ev.backgroundColor || '#C9A44C') + '30', borderWidth: 1 }}>
                  <span className="text-lg leading-none" style={{ fontFamily: 'Playfair Display, serif', color: ev.backgroundColor || '#C9A44C' }}>
                    {new Date(ev.start).getDate()}
                  </span>
                  <span className="text-[9px] uppercase" style={{ color: ev.backgroundColor || '#C9A44C' }}>
                    {new Date(ev.start).toLocaleDateString('es-AR', { month: 'short' })}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{ev.title}</div>
                  <div className="text-[11px] text-[#888]">
                    {fmtHora(ev.start)}
                    {ev.end && ` → ${fmtHora(ev.end)}`}
                    {ev.descripcion && <span> · {ev.descripcion}</span>}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); eliminar(ev); }}
                  className="text-[#888] hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* ─── Modal detalle evento ─────────────────────────── */}
      {eventoSeleccionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setEventoSeleccionado(null)}>
          <div className="w-full max-w-sm bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="w-3 h-3 rounded-full mb-2"
                  style={{ backgroundColor: eventoSeleccionado.backgroundColor || '#C9A44C' }} />
                <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {eventoSeleccionado.title}
                </h3>
              </div>
              <button onClick={() => setEventoSeleccionado(null)} className="text-[#888] hover:text-[#F8F6F0]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2 text-sm mb-5">
              <div className="flex gap-2">
                <Clock className="w-4 h-4 text-[#888] flex-shrink-0 mt-0.5" />
                <span>{fmtFull(eventoSeleccionado.start)}</span>
              </div>
              {eventoSeleccionado.end && (
                <div className="flex gap-2">
                  <Clock className="w-4 h-4 text-[#888] flex-shrink-0 mt-0.5" />
                  <span className="text-[#888]">hasta {fmtHora(eventoSeleccionado.end)}</span>
                </div>
              )}
              {eventoSeleccionado.descripcion && (
                <div className="flex gap-2">
                  <AlignLeft className="w-4 h-4 text-[#888] flex-shrink-0 mt-0.5" />
                  <span className="text-[#ccc]">{eventoSeleccionado.descripcion}</span>
                </div>
              )}
            </div>
            <button onClick={() => eliminar(eventoSeleccionado)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-red-500/25 text-red-400 hover:bg-red-500/10 transition-all text-sm">
              <Trash2 className="w-4 h-4" /> Eliminar evento
            </button>
          </div>
        </div>
      )}

      {showCrear && (
        <ModalCrear fechaInicial={fechaCrear} onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargar(); }} />
      )}

      {/* Modal confirmación eliminar evento (reemplaza el window.confirm nativo) */}
      {eventoAEliminar && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !eliminando) setEventoAEliminar(null); }}>
          <div className="w-full max-w-sm bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg text-[#F8F6F0]" style={{ fontFamily: 'Playfair Display, serif' }}>Eliminar evento</h3>
            </div>
            <p className="text-[#B8B8B8] text-sm leading-relaxed mb-5">
              ¿Seguro que querés eliminar <span className="text-[#F8F6F0] font-medium">"{eventoAEliminar.title || eventoAEliminar.titulo || 'este evento'}"</span>? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setEventoAEliminar(null)} disabled={eliminando}
                className="px-4 py-2.5 text-sm text-[#B8B8B8] hover:text-[#F8F6F0] transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={confirmarEliminar} disabled={eliminando}
                className="px-4 py-2.5 text-sm rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors flex items-center gap-2 disabled:opacity-60">
                {eliminando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {eliminando ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
